<template>
  <section class="billing" aria-label="Store billing">
    <p v-if="!canRead" role="status">You need billing permission to view this store's bills.</p>
    <template v-else>
      <header class="billing__header">
        <h3>Billing</h3>
        <button type="button" title="Refresh billing" aria-label="Refresh billing" :disabled="loading || busy" @click="load">
          <vue-feather type="refresh-cw" size="18" />
        </button>
      </header>
      <p v-if="error" role="alert">{{ error }}</p>
      <p v-if="loading" role="status">Loading billing...</p>
      <template v-else-if="billing">
        <div v-for="(period, index) in billing.periods" :key="index" class="billing__period">
          <strong>{{ period.service_name }}</strong>
          <span>{{ period.currency_code }} {{ formatAmount(period.monthly_price, period.currency_code) }} / month{{ period.billing_mode === 'UPFRONT' ? ' allocation' : '' }}</span>
          <span>{{ date(period.starts_at) }} &ndash; {{ period.ends_at ? date(period.ends_at) : 'Ongoing' }}</span>
        </div>
        <div class="billing__history">
          <h4>Bills</h4>
          <p v-if="!billing.bills.length">No bills yet.</p>
          <div v-else class="billing__table-scroll">
            <table>
              <thead><tr><th>Period</th><th>Amount</th><th>Invoice</th><th>Status</th></tr></thead>
              <tbody>
                <tr v-for="bill in billing.bills" :key="`${bill.billing_kind || 'MONTHLY'}-${bill.id}`">
                  <td>{{ bill.billing_kind === 'UPFRONT' ? `${date(bill.period_start)} - ${date(bill.period_end)} (upfront)` : bill.billing_month }}</td>
                  <td>{{ bill.currency_code }} {{ formatAmount(bill.amount, bill.currency_code) }}</td>
                  <td>{{ bill.sale_cart_id ? `#${bill.sale_cart_id}` : 'No invoice' }}</td>
                  <td>{{ status(bill.invoice_status) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <nav v-if="page > 1 || billing.has_more" aria-label="Billing history pages" class="billing__pagination">
            <button type="button" title="Previous page" aria-label="Previous page" :disabled="page === 1 || loading || busy" @click="changePage(-1)"><vue-feather type="chevron-left" size="18" /></button>
            <span>Page {{ page }}</span>
            <button type="button" title="Next page" aria-label="Next page" :disabled="!billing.has_more || loading || busy" @click="changePage(1)"><vue-feather type="chevron-right" size="18" /></button>
          </nav>
        </div>
        <button v-if="canManage && billing.can_cancel" type="button" class="billing__danger" :disabled="busy" @click="openCancel">Cancel Store Subscription</button>
        <button v-else-if="canManage && billing.resubscribe" type="button" class="billing__subscribe billing__primary" :disabled="busy" @click="openSubscribe">Subscribe Again</button>
      </template>
    </template>
    <dialog v-if="canRead && canManage" ref="cancelDialog" aria-labelledby="billing-cancel-title" class="billing__dialog" @cancel="preventBusyClose">
      <form @submit.prevent="cancel">
        <h3 id="billing-cancel-title">Cancel Store Subscription?</h3>
        <p>This ends this store's subscription immediately, disables renewal and stops future service charges. Existing bills remain payable. Group subscriptions are not cancelled.</p>
        <fieldset class="billing__reasons" :disabled="busy">
          <legend>Reason</legend>
          <label v-for="option in reasonOptions" :key="option.value" class="billing__reason-option">
            <input v-model="reason" type="radio" name="billing-cancel-reason" :value="option.value" />
            <span>{{ option.label }}</span>
          </label>
        </fieldset>
        <template v-if="reason === 'Other'">
          <label for="billing-cancel-note" class="billing__note-label">Additional details (optional)</label>
          <textarea id="billing-cancel-note" v-model="reasonNote" maxlength="505" rows="3" :disabled="busy" />
        </template>
        <p v-if="cancelError" role="alert">{{ cancelError }}</p>
        <footer>
          <button type="button" :disabled="busy" @click="cancelDialog.close()">Keep Subscription</button>
          <button type="submit" class="billing__danger" :disabled="busy || !reason.trim()">{{ busy ? 'Cancelling...' : 'Cancel Now' }}</button>
        </footer>
      </form>
    </dialog>
    <dialog v-if="canRead && canManage && acceptedOffer" ref="subscribeDialog" aria-labelledby="billing-subscribe-title" class="billing__dialog" @cancel="preventBusyClose">
      <form @submit.prevent="subscribeAgain">
        <h3 id="billing-subscribe-title">Subscribe Again?</h3>
        <dl class="billing__terms">
          <dt>Plan</dt><dd>{{ acceptedOffer.plan_name }}</dd>
          <dt>{{ acceptedOffer.billing_mode === 'UPFRONT' ? 'Monthly Allocation' : 'Monthly Price' }}</dt><dd>{{ acceptedOffer.currency_code }} {{ formatAmount(acceptedOffer.monthly_price, acceptedOffer.currency_code) }}</dd>
          <template v-if="acceptedOffer.billing_mode === 'UPFRONT'">
            <dt>Upfront Package</dt><dd>{{ acceptedOffer.currency_code }} {{ formatAmount(acceptedOffer.amount_due, acceptedOffer.currency_code) }} for {{ acceptedOffer.price_period_months }} months</dd>
          </template>
          <dt>Service Starts</dt><dd>{{ acceptedOffer.billing_mode === 'UPFRONT' ? 'After the package invoice is paid' : 'Immediately upon confirmation' }}</dd>
        </dl>
        <p v-if="acceptedOffer.billing_mode === 'UPFRONT'">The full package invoice is created now. Covered months will not be billed again. No payment is collected automatically. Previous bills remain unchanged.</p>
        <p v-else>Service charges resume from today and are prorated for this month. Previous bills remain unchanged. No payment is collected now.</p>
        <p v-if="subscribeError" role="alert">{{ subscribeError }}</p>
        <footer>
          <button type="button" :disabled="busy" @click="subscribeDialog.close()">Not Now</button>
          <button type="submit" class="billing__primary" :disabled="busy">{{ busy ? 'Subscribing...' : 'Confirm Subscription' }}</button>
        </footer>
      </form>
    </dialog>
  </section>
</template>

<script>
import { computed, nextTick, ref, watch } from 'vue';
import http from '@/helper/http';

export default {
  name: 'StoreBillingPanel',
  props: {
    storeId: { type: [Number, String], required: true },
    canRead: { type: Boolean, default: false },
    canManage: { type: Boolean, default: false },
  },
  emits: ['cancelled', 'subscribed'],
  setup(props, { emit }) {
    const billing = ref(null);
    const loading = ref(false);
    const busy = ref(false);
    const error = ref('');
    const cancelError = ref('');
    const subscribeError = ref('');
    const acceptedOffer = ref(null);
    const subscribeDialog = ref(null);
    const reasonOptions = [
      { value: 'Customer requested cancellation', label: 'Prefer not to say' },
      { value: 'No longer needed', label: 'No longer needed' },
      { value: 'Too expensive', label: 'Too expensive' },
      { value: 'Missing features', label: 'Missing features' },
      { value: 'Closing the store', label: 'Closing the store' },
      { value: 'Other', label: 'Other' },
    ];
    const reason = ref('Customer requested cancellation');
    const reasonNote = ref('');
    const cancellationReason = computed(() => reason.value === 'Other' && reasonNote.value.trim()
      ? `Other: ${reasonNote.value.trim()}` : reason.value);
    const page = ref(1);
    const cancelDialog = ref(null);
    let epoch = 0;
    const message = (failure, fallback) => failure?.response?.data?.error?.message || failure?.response?.data?.message || fallback;
    const date = (value) => new Date(value).toLocaleDateString(undefined, { timeZone: 'Africa/Kigali' });
    const status = (value) => ({ CREATED: 'Draft', NO_CHARGE: 'No charge', UNAVAILABLE: 'Unavailable' }[value] || String(value).replace(/_/g, ' ').toLowerCase());
    const formatAmount = (value, currency) => {
      const [whole, fraction = ''] = String(value).split('.');
      const digits = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().minimumFractionDigits;
      const decimal = fraction.replace(/0+$/, '').padEnd(digits, '0');
      return decimal ? `${whole}.${decimal}` : whole;
    };

    async function load() {
      const version = ++epoch;
      if (!props.canRead || !props.storeId) return;
      loading.value = true;
      error.value = '';
      billing.value = null;
      try {
        const response = await http.get(`/v1/stores/${props.storeId}/billing`, { params: { page: page.value } });
        if (version === epoch) billing.value = response.data;
      } catch (failure) {
        if (version === epoch) error.value = message(failure, 'Unable to load billing.');
      } finally {
        if (version === epoch) loading.value = false;
      }
    }

    function changePage(direction) { page.value += direction; load(); }
    function openCancel() {
      reason.value = 'Customer requested cancellation';
      reasonNote.value = '';
      cancelError.value = '';
      cancelDialog.value.showModal();
    }
    function preventBusyClose(event) { if (busy.value) event.preventDefault(); }
    async function openSubscribe() {
      if (busy.value || !props.canManage || !billing.value?.resubscribe) return;
      acceptedOffer.value = { ...billing.value.resubscribe };
      subscribeError.value = '';
      await nextTick();
      subscribeDialog.value?.showModal();
    }
    async function subscribeAgain() {
      if (busy.value || !props.canManage || !acceptedOffer.value) return;
      const storeId = props.storeId;
      const version = epoch;
      busy.value = true;
      subscribeError.value = '';
      try {
        await http.post(`/v1/stores/${storeId}/subscription/resubscribe`, { ...acceptedOffer.value });
        if (version === epoch) {
          subscribeDialog.value?.close();
          acceptedOffer.value = null;
          emit('subscribed');
          await load();
        }
      } catch (failure) {
        if (version === epoch) subscribeError.value = failure?.response?.status === 409
          ? 'Plan availability or pricing changed. Close this dialog and refresh billing before trying again.'
          : message(failure, 'Unable to subscribe again.');
      } finally { busy.value = false; }
    }
    async function cancel() {
      if (busy.value || !props.canManage || !billing.value?.can_cancel || !reason.value.trim()) return;
      const storeId = props.storeId;
      const version = epoch;
      busy.value = true;
      cancelError.value = '';
      try {
        await http.post(`/v1/stores/${storeId}/subscription/cancel`, { reason: cancellationReason.value });
        if (version === epoch) {
          cancelDialog.value?.close();
          emit('cancelled');
          await load();
        }
      } catch (failure) {
        if (version === epoch) cancelError.value = message(failure, 'Unable to cancel the subscription.');
      } finally { busy.value = false; }
    }

    watch(() => [props.storeId, props.canRead, props.canManage], () => {
      cancelDialog.value?.close();
      subscribeDialog.value?.close();
      acceptedOffer.value = null;
      billing.value = null;
      loading.value = false;
      page.value = 1;
      load();
    }, { immediate: true });

    return { billing, loading, busy, error, cancelError, reasonOptions, reason, reasonNote, page, cancelDialog,
      subscribeError, acceptedOffer, subscribeDialog, openSubscribe, subscribeAgain,
      date, status, formatAmount, load, changePage, openCancel, preventBusyClose, cancel };
  },
};
</script>

<style scoped>
.billing { color: inherit; min-width: 0; padding: 1.5rem 0; }
.billing__header, .billing__pagination, .billing__dialog footer { display: flex; align-items: center; gap: .75rem; }
.billing__header { justify-content: space-between; }
.billing h3 { font-size: 1.125rem; font-weight: 600; }
.billing h4 { font-weight: 600; margin: 1.5rem 0 .75rem; }
.billing button { border: 1px solid #9ca3af; border-radius: 6px; padding: .5rem .75rem; display: inline-flex; align-items: center; justify-content: center; }
.billing button:disabled { opacity: .5; cursor: not-allowed; }
.billing__period { display: flex; flex-wrap: wrap; gap: .5rem 1.5rem; padding: 1rem 0; border-bottom: 1px solid #d1d5db; }
.billing__history { margin-bottom: 1.5rem; }
.billing__table-scroll { overflow-x: auto; }
.billing table { width: 100%; border-collapse: collapse; text-align: left; font-size: .875rem; }
.billing th, .billing td { padding: .75rem; border-bottom: 1px solid #d1d5db; white-space: nowrap; }
.billing__pagination { justify-content: flex-end; padding-top: 1rem; }
.billing .billing__danger { color: #b91c1c; border-color: #dc2626; }
.billing .billing__primary { background: var(--hs-primary, #2563eb); color: #fff; border-color: transparent; }
.billing__terms { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: .5rem 1rem; margin-top: 1rem; }
.billing__terms dt { font-weight: 600; }
.billing__terms dd { margin: 0; overflow-wrap: anywhere; }
.billing__dialog { width: min(32rem, calc(100% - 2rem)); max-height: calc(100dvh - 2rem); overflow-y: auto; padding: 1.5rem; border: 1px solid #9ca3af; border-radius: 8px; }
.billing__dialog::backdrop { background: rgb(0 0 0 / .45); }
.billing__dialog p { margin: 1rem 0; line-height: 1.5; }
.billing__dialog label { display: block; margin-bottom: .5rem; }
.billing__reasons { min-width: 0; }
.billing__reasons legend { margin-bottom: .5rem; font-weight: 600; }
.billing__dialog .billing__reason-option { display: flex; align-items: center; gap: .625rem; min-height: 2.5rem; margin: 0; cursor: pointer; }
.billing__reason-option input { flex: 0 0 auto; width: 1rem; height: 1rem; accent-color: var(--hs-primary, #2563eb); }
.billing__reasons:disabled .billing__reason-option { opacity: .6; cursor: default; }
.billing__dialog textarea { width: 100%; border: 1px solid #9ca3af; border-radius: 6px; padding: .5rem; font: inherit; }
.billing__dialog .billing__note-label { margin-top: 1rem; }
.billing__dialog footer { justify-content: flex-end; flex-wrap: wrap; margin-top: 1rem; }
.billing [role=alert] { color: #b91c1c; margin: 1rem 0; }
@media (max-width: 480px) {
  .billing__terms { grid-template-columns: minmax(0, 1fr); gap: .25rem; }
  .billing__terms dd { margin-bottom: .5rem; }
}
</style>